package com.example.crashbit

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.stringResource
import crashbit_multiplatform.composeapp.generated.resources.*

@Composable
fun AboutGameScreen(viewModel: SpaceViewModel) {
    val scrollState = rememberScrollState()
    val neonPurple = Color(0xFFBB86FC)
    val neonCyan = Color(0xFF03DAC5)
    val neonRed = Color(0xFFFF3131)
    val spaceGold = Color(0xFFFFD700)

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                brush = Brush.verticalGradient(
                    colors = listOf(Color(0xFF0D0221), Color.Black)
                )
            ),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .fillMaxHeight()
                .widthIn(max = 800.dp)
                .verticalScroll(scrollState)
                .padding(24.dp)
                .padding(top = 32.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = stringResource(Res.string.app_name).uppercase(),
                color = spaceGold,
                fontSize = 32.sp,
                fontWeight = FontWeight.ExtraBold,
                letterSpacing = 4.sp,
                textAlign = TextAlign.Center
            )
            
            Text(
                text = stringResource(Res.string.version_edition),
                color = Color.White.copy(alpha = 0.5f),
                fontSize = 12.sp,
                fontWeight = FontWeight.Bold,
                letterSpacing = 2.sp
            )

            HorizontalDivider(
                modifier = Modifier
                    .padding(vertical = 24.dp)
                    .width(150.dp),
                thickness = 2.dp,
                color = neonPurple
            )

            Text(
                text = stringResource(Res.string.intro_text),
                color = Color.White.copy(alpha = 0.8f),
                fontSize = 14.sp,
                lineHeight = 22.sp,
                textAlign = TextAlign.Start,
                modifier = Modifier.padding(bottom = 32.dp)
            )

            ManualSectionHeader(stringResource(Res.string.header_health_safety), neonRed)
            BulletItem(
                title = stringResource(Res.string.title_photosensitivity),
                description = stringResource(Res.string.desc_photosensitivity),
                dotColor = neonRed
            )

            Spacer(modifier = Modifier.height(24.dp))

            ManualSectionHeader(stringResource(Res.string.header_quick_start), Color.Yellow)
            BulletItem(
                title = stringResource(Res.string.qs_title_canvas),
                description = stringResource(Res.string.qs_desc_canvas),
                dotColor = Color.Yellow
            )
            BulletItem(
                title = stringResource(Res.string.qs_title_yeet),
                description = stringResource(Res.string.qs_desc_yeet),
                dotColor = spaceGold
            )
            BulletItem(
                title = stringResource(Res.string.qs_title_rain),
                description = stringResource(Res.string.qs_desc_rain),
                dotColor = neonPurple
            )
            BulletItem(
                title = stringResource(Res.string.qs_title_inspect),
                description = stringResource(Res.string.qs_desc_inspect),
                dotColor = neonCyan
            )

            Spacer(modifier = Modifier.height(24.dp))

            ManualSectionHeader(stringResource(Res.string.header_choosing_path), neonPurple)
            BulletItem(
                title = stringResource(Res.string.path_title_chaos),
                description = stringResource(Res.string.path_desc_chaos),
                dotColor = neonRed
            )

            ManualSectionHeader(stringResource(Res.string.header_interstellar_command), neonCyan)
            BulletItem(
                title = stringResource(Res.string.cmd_title_yeeting),
                description = stringResource(Res.string.cmd_desc_yeeting),
                dotColor = spaceGold
            )
            BulletItem(
                title = stringResource(Res.string.cmd_title_rain),
                description = stringResource(Res.string.cmd_desc_rain),
                dotColor = neonPurple
            )
            BulletItem(
                title = stringResource(Res.string.cmd_title_nav),
                description = stringResource(Res.string.cmd_desc_nav),
                dotColor = Color.White
            )

            ManualSectionHeader(stringResource(Res.string.header_advanced_observation), spaceGold)
            BulletItem(
                title = stringResource(Res.string.obs_title_camera),
                description = stringResource(Res.string.obs_desc_camera),
                dotColor = neonCyan
            )
            BulletItem(
                title = stringResource(Res.string.obs_title_farrell),
                description = stringResource(Res.string.obs_desc_farrell),
                dotColor = Color(0xFF03DAC5)
            )
            BulletItem(
                title = stringResource(Res.string.obs_title_scanner),
                description = stringResource(Res.string.obs_desc_scanner),
                dotColor = Color.White
            )
            BulletItem(
                title = stringResource(Res.string.obs_title_oracle),
                description = stringResource(Res.string.obs_desc_oracle),
                dotColor = Color.Red
            )
            BulletItem(
                title = stringResource(Res.string.obs_title_fast_travel),
                description = stringResource(Res.string.obs_desc_fast_travel),
                dotColor = Color.Cyan
            )
            
            ManualSectionHeader(stringResource(Res.string.header_galactic_phenomena), neonPurple)
            BulletItem(
                title = stringResource(Res.string.phen_title_evolution),
                description = stringResource(Res.string.phen_desc_evolution),
                dotColor = spaceGold
            )
            BulletItem(
                title = stringResource(Res.string.phen_title_nebulae),
                description = stringResource(Res.string.phen_desc_nebulae),
                dotColor = Color(0xFF8B00FF)
            )
            BulletItem(
                title = stringResource(Res.string.phen_title_jordys),
                description = stringResource(Res.string.phen_desc_jordys),
                dotColor = Color.Magenta
            )

            if (!isSaveSupported) {
                Spacer(modifier = Modifier.height(12.dp))
                Surface(
                    color = Color.Yellow.copy(alpha = 0.1f),
                    shape = RoundedCornerShape(12.dp),
                    border = BorderStroke(1.dp, Color.Yellow.copy(alpha = 0.4f)),
                    modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp)
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        Text(
                            "🖱️ BROWSER MOUSE & SCROLL NAVIGATION",
                            color = Color.Yellow,
                            fontSize = 13.sp,
                            fontWeight = FontWeight.ExtraBold
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                        Text(
                            "Playing in the browser? Use your mouse pointer to click and drag (with Input set to OFF) to pan around the universe, and use your mouse scroll wheel to zoom in and out effortlessly.",
                            color = Color.White.copy(alpha = 0.8f),
                            fontSize = 11.sp
                        )
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))
                Surface(
                    color = Color.Cyan.copy(alpha = 0.1f),
                    shape = RoundedCornerShape(12.dp),
                    border = BorderStroke(1.dp, Color.Cyan.copy(alpha = 0.4f)),
                    modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp)
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        Text(
                            "🌀 WORMHOLES & SPACE-TIME WARPING",
                            color = Color.Cyan,
                            fontSize = 13.sp,
                            fontWeight = FontWeight.ExtraBold
                        )
                        Spacer(modifier = Modifier.height(4.dp))
                        Text(
                            "Space-time warping wormholes, universe saving, and offline play are exclusive features of the upcoming CrashBit Mobile App on iOS & Android! Stay tuned!",
                            color = Color.White.copy(alpha = 0.8f),
                            fontSize = 11.sp
                        )
                    }
                }
            } else {
                BulletItem(
                    title = stringResource(Res.string.phen_title_wormholes),
                    description = stringResource(Res.string.phen_desc_wormholes),
                    dotColor = Color.Cyan
                )
            }

            ManualSectionHeader(stringResource(Res.string.header_cosmic_lab), neonCyan)
            BulletItem(
                title = stringResource(Res.string.lab_title_options),
                description = stringResource(Res.string.lab_desc_options),
                dotColor = neonCyan
            )
            BulletItem(
                title = stringResource(Res.string.lab_title_perf),
                description = stringResource(Res.string.lab_desc_perf),
                dotColor = Color.Yellow
            )

            ManualSectionHeader(stringResource(Res.string.header_time_mastery_manual), Color.White)
            BulletItem(
                title = stringResource(Res.string.tm_title_dilation),
                description = stringResource(Res.string.tm_desc_dilation),
                dotColor = neonCyan
            )
            BulletItem(
                title = stringResource(Res.string.tm_title_rewind),
                description = stringResource(Res.string.tm_desc_rewind),
                dotColor = Color.Yellow
            )

            ManualSectionHeader(stringResource(Res.string.header_physics_fidgeting), neonCyan)
            BulletItem(
                title = stringResource(Res.string.phys_title_science),
                description = stringResource(Res.string.phys_desc_science),
                dotColor = Color.White
            )
            BulletItem(
                title = stringResource(Res.string.phys_title_wells),
                description = stringResource(Res.string.phys_desc_wells),
                dotColor = neonCyan
            )
            BulletItem(
                title = stringResource(Res.string.phys_title_barycenter),
                description = stringResource(Res.string.phys_desc_barycenter),
                dotColor = spaceGold
            )
            BulletItem(
                title = stringResource(Res.string.phys_title_gscaling),
                description = stringResource(Res.string.phys_desc_gscaling),
                dotColor = neonRed
            )
            BulletItem(
                title = stringResource(Res.string.phys_title_tweaks),
                description = stringResource(Res.string.phys_desc_tweaks),
                dotColor = spaceGold
            )

            val uriHandler = LocalUriHandler.current

            ManualSectionHeader(stringResource(Res.string.header_contact_feedback), neonCyan)
            Surface(
                color = Color.Cyan.copy(alpha = 0.08f),
                shape = RoundedCornerShape(12.dp),
                border = BorderStroke(1.dp, Color.Cyan.copy(alpha = 0.35f)),
                modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp).clickable {
                    uriHandler.openUri("mailto:contact@crashbitfidget.com")
                }
            ) {
                Column(modifier = Modifier.padding(16.dp)) {
                    Text(
                        stringResource(Res.string.label_contact_questions),
                        color = Color.White,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Bold
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        stringResource(Res.string.label_contact_email) + " (Tap to send email)",
                        color = Color.Cyan,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Bold
                    )
                }
            }

            ManualSectionHeader(stringResource(Res.string.title_changelog), spaceGold)
            defaultChangelogEntries.forEach { entry ->
                Surface(
                    color = Color.White.copy(alpha = 0.05f),
                    shape = RoundedCornerShape(12.dp),
                    border = BorderStroke(1.dp, spaceGold.copy(alpha = 0.3f)),
                    modifier = Modifier.fillMaxWidth().padding(vertical = 6.dp)
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(entry.version, color = spaceGold, fontSize = 13.sp, fontWeight = FontWeight.ExtraBold)
                            Text(entry.date, color = Color.White.copy(alpha = 0.5f), fontSize = 11.sp, fontWeight = FontWeight.Bold)
                        }
                        Spacer(modifier = Modifier.height(6.dp))
                        entry.highlights.forEach { item ->
                            Text("• $item", color = Color.White.copy(alpha = 0.85f), fontSize = 11.sp, lineHeight = 15.sp, modifier = Modifier.padding(vertical = 2.dp))
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(48.dp))

            Button(
                onClick = { viewModel.backToTitle() },
                colors = ButtonDefaults.buttonColors(
                    containerColor = neonPurple,
                    contentColor = Color.Black
                ),
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier
                    .fillMaxWidth()
                    .height(56.dp)
            ) {
                Text(
                    text = stringResource(Res.string.btn_return_to_command),
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp
                )
            }
            
            Spacer(modifier = Modifier.height(32.dp))
        }
    }
}

@Composable
fun ManualSectionHeader(text: String, color: Color) {
    Text(
        text = text.uppercase(),
        color = color,
        fontSize = 14.sp,
        fontWeight = FontWeight.ExtraBold,
        letterSpacing = 2.sp,
        modifier = Modifier
            .fillMaxWidth()
            .padding(bottom = 12.dp)
    )
}

@Composable
fun BulletItem(title: String, description: String, dotColor: Color) {
    Row(modifier = Modifier.padding(bottom = 20.dp)) {
        Text(
            text = "•",
            color = dotColor,
            fontSize = 20.sp,
            modifier = Modifier.padding(end = 12.dp)
        )
        Column {
            Text(
                text = title,
                color = Color.White,
                fontSize = 15.sp,
                fontWeight = FontWeight.Bold
            )
            Text(
                text = description,
                color = Color.White.copy(alpha = 0.6f),
                fontSize = 13.sp,
                lineHeight = 20.sp
            )
        }
    }
}
